/**
 * CreateCommute.js — Pendler-ANGEBOT erstellen (Creator-Mode)
 * ─────────────────────────────────────────────────────────────
 * Driver-side: ein Pendler-Angebot mit Hin- (morning_time) und
 * Rückfahrt (return_time), wiederkehrend an gewählten Wochentagen.
 *
 * Backend: POST /api/commute-offers
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  MapPin, Clock, Loader2, Repeat, AlertCircle, Tag, ArrowRight, ArrowLeft,
  Map as MapIcon, Users, Route as RouteIcon, Sunrise, Sunset,
} from 'lucide-react';
import { AddressAutocomplete } from '../components/AddressAutocomplete';
import MapPickerModal from '../components/MapPickerModal';
import { API } from '../utils/apiConfig';

const WEEKDAYS = [
  { value: 0, short: 'Mo' }, { value: 1, short: 'Di' }, { value: 2, short: 'Mi' },
  { value: 3, short: 'Do' }, { value: 4, short: 'Fr' }, { value: 5, short: 'Sa' },
  { value: 6, short: 'So' },
];

const PRESETS = [
  { label: 'Mo–Fr', days: [0, 1, 2, 3, 4] },
  { label: 'Wochenende', days: [5, 6] },
  { label: 'Täglich', days: [0, 1, 2, 3, 4, 5, 6] },
];

export const CreateCommute = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [fromCity, setFromCity] = useState('');
  const [toCity, setToCity] = useState('');
  const [fromCoords, setFromCoords] = useState(null);
  const [toCoords, setToCoords] = useState(null);
  const [weekdays, setWeekdays] = useState([0, 1, 2, 3, 4]);
  const [morningTime, setMorningTime] = useState('08:00');
  const [returnTime, setReturnTime] = useState('17:00');
  const [seatsTotal, setSeatsTotal] = useState(3);
  const [maxDetourKm, setMaxDetourKm] = useState(5);
  const [label, setLabel] = useState('');
  const [mapPickerOpen, setMapPickerOpen] = useState(null); // 'from' | 'to' | null
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const toggleDay = (d) =>
    setWeekdays((p) => p.includes(d) ? p.filter(x => x !== d) : [...p, d].sort());

  const handleMapPickerSelect = (data) => {
    const display = data.display_name || data.short_name || data.city || '';
    if (mapPickerOpen === 'from') {
      setFromCity(display);
      setFromCoords({ lat: data.lat, lng: data.lng });
    } else if (mapPickerOpen === 'to') {
      setToCity(display);
      setToCoords({ lat: data.lat, lng: data.lng });
    }
    setMapPickerOpen(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!fromCity.trim() || !toCity.trim()) {
      return setError(t('commute.errFromTo', 'Bitte Start und Ziel angeben.'));
    }
    if (weekdays.length === 0) {
      return setError(t('commute.errWeekdays', 'Bitte mindestens einen Wochentag auswählen.'));
    }
    if (!morningTime && !returnTime) {
      return setError(t('commute.errOneTime', 'Mindestens eine Uhrzeit (Hin oder Rück) angeben.'));
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/commute-offers`, {
        from_city: fromCity, to_city: toCity,
        from_lat: fromCoords?.lat, from_lng: fromCoords?.lng,
        to_lat: toCoords?.lat, to_lng: toCoords?.lng,
        weekdays,
        morning_time: morningTime || null,
        return_time: returnTime || null,
        seats_total: seatsTotal,
        max_detour_km: maxDetourKm,
        label: label.trim() || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      navigate('/my-rides?tab=commutes');
    } catch (err) {
      setError(err.response?.data?.detail || t('commute.errSave', 'Speichern fehlgeschlagen.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="create-commute-page" style={s.page}>
      <div style={s.card}>
        <div style={s.brandStripe}>
          <Repeat size={14} color="#a855f7" />
          <span>VoiceRide · Pendler-Angebot</span>
        </div>
        <h1 style={s.title}>{t('commute.createOfferTitle', 'Pendler-Angebot erstellen')}</h1>
        <p style={s.subtitle}>
          {t('commute.createOfferSubtitle',
            'Lege deine wiederkehrende Pendler-Strecke an. Wir bringen dich automatisch mit Mitfahrern zusammen — Hin- und Rückfahrt mit einem Klick.')}
        </p>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>

          {/* ── ROUTE BLOCK ─────────────────────────────────────── */}
          <section style={s.section}>
            <h3 style={s.sectionTitle}><RouteIcon size={15} /> {t('commute.routeTitle', 'Strecke')}</h3>

            <div style={s.fieldGroup}>
              <label style={s.label}>
                <MapPin size={13} /> {t('commute.from', 'Start')}
              </label>
              <div style={s.inputRow}>
                <div style={{ flex: 1 }}>
                  <AddressAutocomplete
                    value={fromCity}
                    onChange={setFromCity}
                    onSelect={(item) => {
                      setFromCity(item.poi_name || item.short_name || item.city || item.display_name || '');
                      if (item.lat && item.lng) setFromCoords({ lat: item.lat, lng: item.lng });
                    }}
                    placeholder={t('commute.fromPlaceholder', 'z. B. Hamburg, Reeperbahn')}
                    testId="commute-from-input"
                  />
                </div>
                <button type="button" onClick={() => setMapPickerOpen('from')}
                        style={{ ...s.mapBtn, ...(fromCoords ? s.mapBtnActive : {}) }}
                        data-testid="commute-from-map-btn"
                        title={t('commute.pickOnMap', 'Auf Karte wählen')}>
                  <MapIcon size={16} />
                </button>
              </div>
              {fromCoords && (
                <span style={s.coordHint} data-testid="commute-from-coords">
                  📍 {fromCoords.lat.toFixed(4)}, {fromCoords.lng.toFixed(4)}
                </span>
              )}
            </div>

            <div style={s.fieldGroup}>
              <label style={s.label}>
                <ArrowRight size={13} /> {t('commute.to', 'Ziel')}
              </label>
              <div style={s.inputRow}>
                <div style={{ flex: 1 }}>
                  <AddressAutocomplete
                    value={toCity}
                    onChange={setToCity}
                    onSelect={(item) => {
                      setToCity(item.poi_name || item.short_name || item.city || item.display_name || '');
                      if (item.lat && item.lng) setToCoords({ lat: item.lat, lng: item.lng });
                    }}
                    placeholder={t('commute.toPlaceholder', 'z. B. Berlin, Alexanderplatz')}
                    testId="commute-to-input"
                  />
                </div>
                <button type="button" onClick={() => setMapPickerOpen('to')}
                        style={{ ...s.mapBtn, ...(toCoords ? s.mapBtnActive : {}) }}
                        data-testid="commute-to-map-btn"
                        title={t('commute.pickOnMap', 'Auf Karte wählen')}>
                  <MapIcon size={16} />
                </button>
              </div>
              {toCoords && (
                <span style={s.coordHint} data-testid="commute-to-coords">
                  📍 {toCoords.lat.toFixed(4)}, {toCoords.lng.toFixed(4)}
                </span>
              )}
            </div>
          </section>

          {/* ── DAYS ─────────────────────────────────────────────── */}
          <section style={s.section}>
            <h3 style={s.sectionTitle}>
              <Repeat size={15} /> {t('commute.daysTitle', 'An welchen Tagen?')}
            </h3>
            <div style={s.weekdayRow} data-testid="commute-weekdays">
              {WEEKDAYS.map(({ value, short }) => {
                const active = weekdays.includes(value);
                return (
                  <button key={value} type="button" onClick={() => toggleDay(value)}
                          data-testid={`commute-weekday-${value}`}
                          style={{ ...s.dayBtn, ...(active ? s.dayBtnActive : {}) }}>
                    {short}
                  </button>
                );
              })}
            </div>
            <div style={s.presetRow}>
              {PRESETS.map(p => (
                <button key={p.label} type="button" onClick={() => setWeekdays(p.days)}
                        style={s.presetBtn}
                        data-testid={`commute-preset-${p.label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
                  {p.label}
                </button>
              ))}
            </div>
          </section>

          {/* ── TWO TIMES ────────────────────────────────────────── */}
          <section style={s.section}>
            <h3 style={s.sectionTitle}>
              <Clock size={15} /> {t('commute.timesTitle', 'Hin- und Rückfahrt')}
            </h3>

            <div style={s.timesGrid}>
              <div style={s.timeBox}>
                <label style={{ ...s.label, color: '#fde68a' }}>
                  <Sunrise size={13} /> {t('commute.morningTime', 'Hinfahrt')}
                </label>
                <input type="time" value={morningTime}
                       onChange={(e) => setMorningTime(e.target.value)}
                       data-testid="commute-morning-time"
                       style={s.timeInput} />
                <span style={s.timeHelp}>
                  {fromCity || '—'} <ArrowRight size={11} /> {toCity || '—'}
                </span>
              </div>

              <div style={s.timeBox}>
                <label style={{ ...s.label, color: '#fda4af' }}>
                  <Sunset size={13} /> {t('commute.returnTime', 'Rückfahrt')}
                </label>
                <input type="time" value={returnTime}
                       onChange={(e) => setReturnTime(e.target.value)}
                       data-testid="commute-return-time"
                       style={s.timeInput} />
                <span style={s.timeHelp}>
                  {toCity || '—'} <ArrowLeft size={11} /> {fromCity || '—'}
                </span>
              </div>
            </div>
            <p style={s.helperText}>
              {t('commute.timesHelp',
                'Lass eine Zeit leer, wenn du z. B. nur eine Hinfahrt anbietest.')}
            </p>
          </section>

          {/* ── PARAMS ───────────────────────────────────────────── */}
          <section style={s.section}>
            <h3 style={s.sectionTitle}>
              <Users size={15} /> {t('commute.paramsTitle', 'Plätze & Korridor')}
            </h3>

            <div style={s.paramsGrid}>
              <div>
                <label style={s.label}><Users size={13} /> {t('commute.seats', 'Sitzplätze')}</label>
                <div style={s.stepperRow}>
                  <button type="button"
                          onClick={() => setSeatsTotal((v) => Math.max(1, v - 1))}
                          style={s.stepBtn}
                          data-testid="commute-seats-minus">−</button>
                  <span style={s.stepperValue} data-testid="commute-seats-value">{seatsTotal}</span>
                  <button type="button"
                          onClick={() => setSeatsTotal((v) => Math.min(8, v + 1))}
                          style={s.stepBtn}
                          data-testid="commute-seats-plus">+</button>
                </div>
              </div>

              <div>
                <label style={s.label}>
                  <RouteIcon size={13} /> {t('commute.detour', 'Max. Abweichung (km)')}
                </label>
                <div style={s.sliderRow}>
                  <input type="range" min={0} max={20} step={0.5}
                         value={maxDetourKm}
                         onChange={(e) => setMaxDetourKm(parseFloat(e.target.value))}
                         data-testid="commute-detour-slider"
                         style={s.slider} />
                  <span style={s.sliderVal}>{maxDetourKm} km</span>
                </div>
              </div>
            </div>
          </section>

          {/* ── LABEL ───────────────────────────────────────────── */}
          <div>
            <label style={s.label}>
              <Tag size={13} /> {t('commute.labelOptional', 'Bezeichnung (optional)')}
            </label>
            <input type="text" value={label}
                   onChange={(e) => setLabel(e.target.value)}
                   maxLength={120}
                   placeholder={t('commute.labelPlaceholder', 'z. B. Zur Arbeit')}
                   data-testid="commute-label-input"
                   style={s.textInput} />
          </div>

          {error && (
            <div style={s.errorBox} data-testid="commute-error-msg">
              <AlertCircle size={16} /> {error}
            </div>
          )}

          <button type="submit" disabled={loading} style={{ ...s.cta, opacity: loading ? 0.6 : 1 }}
                  data-testid="commute-submit-btn">
            {loading
              ? <><Loader2 size={16} className="spin" /> {t('common.saving', 'Speichern…')}</>
              : <><Repeat size={16} /> {t('commute.submitOfferCta', 'Pendler-Angebot erstellen')}</>}
          </button>
        </form>
      </div>

      <MapPickerModal
        open={!!mapPickerOpen}
        onClose={() => setMapPickerOpen(null)}
        onSelect={handleMapPickerSelect}
        label={mapPickerOpen === 'from'
          ? t('commute.pickFrom', 'Startpunkt wählen')
          : t('commute.pickTo', 'Ziel wählen')}
      />
    </div>
  );
};

export default CreateCommute;

// ── Styles (Glassmorphism) ────────────────────────────────────────
const s = {
  page: {
    minHeight: '100vh', display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    background: 'radial-gradient(circle at 30% 0%, #1e1b4b 0%, #0b1220 60%, #020617 100%)',
    color: '#f1f5f9', padding: '40px 18px',
  },
  card: {
    width: '100%', maxWidth: 640,
    background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(22px)', WebkitBackdropFilter: 'blur(22px)',
    borderWidth: 1, borderStyle: 'solid', borderColor: 'rgba(255,255,255,0.08)',
    borderRadius: 22,
    padding: 28, boxShadow: '0 32px 80px rgba(0,0,0,0.55)',
    display: 'flex', flexDirection: 'column', gap: 18,
  },
  brandStripe: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#a3a3a3', textTransform: 'uppercase', letterSpacing: 1 },
  title: { margin: 0, fontSize: 24, fontWeight: 700 },
  subtitle: { margin: 0, fontSize: 13, color: '#94a3b8', lineHeight: 1.5 },
  section: {
    background: 'rgba(255,255,255,0.025)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 16, padding: 18,
    display: 'flex', flexDirection: 'column', gap: 14,
  },
  sectionTitle: {
    display: 'flex', alignItems: 'center', gap: 8,
    margin: 0, fontSize: 13, fontWeight: 700, color: '#e2e8f0',
    textTransform: 'uppercase', letterSpacing: 0.6,
  },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: 4 },
  label: {
    display: 'flex', alignItems: 'center', gap: 6,
    fontSize: 11, fontWeight: 600, color: '#cbd5e1',
    textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4,
  },
  inputRow: { display: 'flex', gap: 8, alignItems: 'flex-start' },
  textInput: {
    width: '100%', padding: '11px 14px', borderRadius: 12,
    borderWidth: 1, borderStyle: 'solid', borderColor: 'rgba(255,255,255,0.1)',
    background: 'rgba(255,255,255,0.04)', color: '#f1f5f9',
    fontSize: 14, outline: 'none',
  },
  mapBtn: {
    width: 44, height: 44, padding: 0, borderRadius: 12, marginTop: 2,
    borderWidth: 1, borderStyle: 'solid', borderColor: 'rgba(168,85,247,0.4)',
    background: 'rgba(168,85,247,0.1)', color: '#e9d5ff', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  mapBtnActive: {
    background: 'linear-gradient(135deg, rgba(34,197,94,0.25), rgba(168,85,247,0.25))',
    borderColor: 'rgba(34,197,94,0.5)', color: '#bbf7d0',
  },
  coordHint: { fontSize: 11, color: '#86efac', marginTop: 4 },
  weekdayRow: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  dayBtn: {
    flex: '1 1 calc(14.28% - 8px)', minWidth: 44,
    padding: '10px 6px', borderRadius: 12,
    borderWidth: 1, borderStyle: 'solid', borderColor: 'rgba(255,255,255,0.1)',
    background: 'rgba(255,255,255,0.04)', color: '#cbd5e1',
    fontSize: 13, fontWeight: 700, cursor: 'pointer',
    transition: 'all 0.18s ease',
  },
  dayBtnActive: {
    background: 'linear-gradient(135deg, rgba(168,85,247,0.4), rgba(34,197,94,0.3))',
    color: '#fff', borderColor: 'rgba(168,85,247,0.6)',
    boxShadow: '0 6px 18px rgba(168,85,247,0.25)',
  },
  presetRow: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  presetBtn: {
    padding: '5px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600,
    borderWidth: 1, borderStyle: 'solid', borderColor: 'rgba(255,255,255,0.08)',
    background: 'rgba(255,255,255,0.03)', color: '#94a3b8', cursor: 'pointer',
  },
  timesGrid: {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12,
  },
  timeBox: {
    background: 'rgba(255,255,255,0.04)', borderRadius: 12,
    padding: 14, display: 'flex', flexDirection: 'column', gap: 8,
    borderWidth: 1, borderStyle: 'solid', borderColor: 'rgba(255,255,255,0.06)',
  },
  timeInput: {
    padding: '10px 12px', borderRadius: 10,
    borderWidth: 1, borderStyle: 'solid', borderColor: 'rgba(255,255,255,0.1)',
    background: 'rgba(0,0,0,0.25)', color: '#f1f5f9',
    fontSize: 16, fontWeight: 700, outline: 'none',
  },
  timeHelp: {
    display: 'flex', alignItems: 'center', gap: 4,
    fontSize: 11, color: '#94a3b8',
  },
  helperText: { fontSize: 11, color: '#64748b', margin: 0 },
  paramsGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  stepperRow: {
    display: 'flex', alignItems: 'center', gap: 8,
    background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: 4,
    width: 'fit-content',
  },
  stepBtn: {
    width: 32, height: 32, borderRadius: 8, border: 'none',
    background: 'rgba(168,85,247,0.2)', color: '#e9d5ff',
    fontSize: 18, fontWeight: 700, cursor: 'pointer',
  },
  stepperValue: {
    minWidth: 28, textAlign: 'center', fontSize: 16, fontWeight: 700, color: '#f1f5f9',
  },
  sliderRow: { display: 'flex', alignItems: 'center', gap: 10 },
  slider: { flex: 1, accentColor: '#a855f7' },
  sliderVal: { fontSize: 13, fontWeight: 700, color: '#bbf7d0', minWidth: 50, textAlign: 'right' },
  errorBox: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '10px 12px', borderRadius: 10,
    background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)',
    color: '#fecaca', fontSize: 13,
  },
  cta: {
    padding: '14px 18px', borderRadius: 14, border: 'none',
    background: 'linear-gradient(135deg, #a855f7, #22c55e)',
    color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    boxShadow: '0 12px 32px rgba(168,85,247,0.3)',
  },
};
